import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import infoIcon from '@/assets/drbfm/info.svg';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { listCADModels } from '../../services/cad.service';
import { resolveApiErrorMessage } from '../../utils/apiErrorMessages';
import type { CADModel } from '../../types/cad';

/**
 * Radix throws on `value=""`, so the "no model" row needs a stand-in that is
 * mapped back to `undefined` before it ever reaches the caller.
 */
const NO_MODEL = '__cad_model_select_none__';

interface CADModelSelectProps {
  value?: string;
  onChange: (cadModelId: string | undefined) => void;
  disabled?: boolean;
  helperText?: string;
  placeholder?: string;
  helperPlacement?: 'below' | 'label';
  label?: string;
}

export const CADModelSelect: React.FC<CADModelSelectProps> = ({
  value = '',
  onChange,
  disabled = false,
  helperText,
  placeholder,
  helperPlacement = 'below',
  label,
}) => {
  const { t } = useTranslation();
  const [models, setModels] = useState<CADModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  // Held unresolved so the model-list effect stays keyed on `disabled` alone;
  // the copy is derived at render.
  const [loadError, setLoadError] = useState<unknown>(null);
  const selectLabel = label ?? t('cad.modelSelect.label', 'CADモデル');
  const id = useId();
  const labelId = `${id}-label`;
  const triggerId = `${id}-trigger`;
  const helpId = `${id}-help`;

  useEffect(() => {
    if (disabled) return;

    let cancelled = false;
    setIsLoading(true);
    setLoadError(null);

    listCADModels()
      .then((response) => {
        if (!cancelled) {
          setModels(response.models || []);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(err ?? new Error('listCADModels rejected'));
          setModels([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [disabled]);

  const completedModels = useMemo(
    () => models.filter((model) => model.status === 'completed'),
    [models],
  );

  const handleChange = (nextValue: string) => {
    onChange(nextValue === NO_MODEL ? undefined : nextValue);
  };

  const help =
    completedModels.length === 0 && !isLoading
      ? t('cad.modelSelect.empty', '利用可能なCADモデルがありません')
      : helperText;

  return (
    <div>
      <div className={helperPlacement === 'label' ? 'mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-1' : undefined}>
        <Label
          id={labelId}
          htmlFor={triggerId}
          className={`revy-cjk block text-sm font-medium leading-[1.5]${helperPlacement === 'below' ? ' mb-1.5' : ''}`}
        >
          {selectLabel}
        </Label>
        {helperPlacement === 'label' && help && (
          <p id={helpId} className="revy-cjk flex items-start gap-1 text-xs leading-[1.7] text-muted-foreground">
            <img src={infoIcon} alt="" className="shrink-0" />
            {help}
          </p>
        )}
      </div>

      <Select
        value={value || NO_MODEL}
        onValueChange={handleChange}
        disabled={disabled || isLoading}
      >
        {/* Radix only sets the native `disabled`; MUI also exposed
            aria-disabled, which is the signal callers wait on before opening. */}
        <SelectTrigger
          id={triggerId}
          aria-labelledby={labelId}
          aria-describedby={help ? helpId : undefined}
          aria-disabled={disabled || isLoading || undefined}
          data-empty={!value || undefined}
          className="revy-cjk"
        >
          <SelectValue>{!value && placeholder ? placeholder : undefined}</SelectValue>
          {isLoading && <Spinner size="sm" label="" className="ml-auto mr-1 text-muted-foreground" />}
        </SelectTrigger>

        <SelectContent className="revy-ui">
          <SelectItem value={NO_MODEL} className="revy-cjk">
            {t('cad.modelSelect.none', 'CADモデルを選択しない')}
          </SelectItem>
          {completedModels.map((model) => (
            <SelectItem key={model.id} value={model.id} className="revy-cjk">
              {model.filename}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {helperPlacement === 'below' && help && (
        <p id={helpId} className="revy-cjk mt-1.5 text-xs leading-[1.7] text-muted-foreground">
          {help}
        </p>
      )}

      {loadError != null && (
        <Alert variant="warning" className="mt-2">
          <AlertDescription className="revy-cjk leading-[1.6]">
            {resolveApiErrorMessage(t, loadError, { fallbackKey: 'cad.modelSelect.loadFailed' })}
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default CADModelSelect;
